import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FilterSidebar from './components/FilterSidebar';
import SearchHeader from './components/SearchHeader';
import TourCard from './components/TourCard';
import MapView from './components/MapView';
import LoadingSkeleton from './components/LoadingSkeleton';
import EmptyState from './components/EmptyState';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State management
  const [tours, setTours] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams?.get('q') || '');
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('list');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [selectedTour, setSelectedTour] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Mock tour data
  const mockTours = [
    {
      id: 1,
      title: "Bali Temple Hopping & Cultural Experience",
      description: "Discover the spiritual heart of Bali with visits to ancient temples, traditional villages, and sacred sites. Experience authentic Balinese culture and witness stunning architecture.",
      image: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?w=800&h=600&fit=crop",
      location: "Ubud, Bali",
      category: "Cultural & Historical",
      price: 89,
      originalPrice: 120,
      discount: 25,
      rating: 4.8,
      reviewCount: 324,
      duration: "8 hours",
      maxGroupSize: 12,
      difficulty: "Easy",
      availability: "high",
      isInstantBooking: true,
      isWishlisted: false,
      features: ["Hotel Pickup", "Local Guide", "Traditional Lunch", "Temple Entry Fees"]
    },
    {
      id: 2,
      title: "Paris Evening Seine River Cruise with Dinner",
      description: "Enjoy a romantic evening cruise along the Seine River with panoramic views of Paris landmarks. Includes a gourmet French dinner and live entertainment.",
      image: "https://images.unsplash.com/photo-1502602898536-47ad22581b52?w=800&h=600&fit=crop",
      location: "Paris, France",
      category: "City Tours",
      price: 145,
      originalPrice: null,
      discount: null,
      rating: 4.6,
      reviewCount: 892,
      duration: "3 hours",
      maxGroupSize: 50,
      difficulty: "Easy",
      availability: "medium",
      isInstantBooking: true,
      isWishlisted: true,
      features: ["Dinner Included", "Live Music", "Panoramic Views", "Welcome Drink"]
    },
    {
      id: 3,
      title: "Costa Rica Zip Line & Wildlife Adventure",
      description: "Soar through the rainforest canopy on thrilling zip lines while spotting exotic wildlife. Perfect combination of adventure and nature exploration.",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
      location: "Manuel Antonio, Costa Rica",
      category: "Adventure & Outdoor",
      price: 95,
      originalPrice: 110,
      discount: 15,
      rating: 4.9,
      reviewCount: 567,
      duration: "6 hours",
      maxGroupSize: 8,
      difficulty: "Moderate",
      availability: "low",
      isInstantBooking: false,
      isWishlisted: false,
      features: ["Safety Equipment", "Professional Guide", "Wildlife Spotting", "Transportation"]
    },
    {
      id: 4,
      title: "Tokyo Food Tour: Street Food & Local Markets",
      description: "Explore Tokyo's vibrant food scene with a local guide. Visit traditional markets, try authentic street food, and discover hidden culinary gems.",
      image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&h=600&fit=crop",
      location: "Shibuya, Tokyo",
      category: "Food & Drink",
      price: 78,
      originalPrice: null,
      discount: null,
      rating: 4.7,
      reviewCount: 445,
      duration: "4 hours",
      maxGroupSize: 10,
      difficulty: "Easy",
      availability: "high",
      isInstantBooking: true,
      isWishlisted: false,
      features: ["Food Tastings", "Local Guide", "Market Visits", "Recipe Cards"]
    },
    {
      id: 5,
      title: "Serengeti Safari: Big Five Wildlife Experience",
      description: "Embark on an unforgettable safari adventure in the Serengeti. Witness the Big Five and experience the Great Migration in their natural habitat.",
      image: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&h=600&fit=crop",
      location: "Serengeti, Tanzania",
      category: "Nature & Wildlife",
      price: 450,
      originalPrice: 520,
      discount: 13,
      rating: 4.9,
      reviewCount: 234,
      duration: "3 days",
      maxGroupSize: 6,
      difficulty: "Moderate",
      availability: "medium",
      isInstantBooking: false,
      isWishlisted: true,
      features: ["Game Drives", "Professional Guide", "Accommodation", "All Meals"]
    },
    {
      id: 6,
      title: "Northern Lights Photography Tour",
      description: "Chase the magical Northern Lights in Iceland with professional photography guidance. Learn techniques to capture the aurora borealis perfectly.",
      image: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&h=600&fit=crop",
      location: "Reykjavik, Iceland",
      category: "Photography Tours",
      price: 125,
      originalPrice: 150,
      discount: 17,
      rating: 4.8,
      reviewCount: 189,
      duration: "6 hours",
      maxGroupSize: 8,
      difficulty: "Moderate",
      availability: "low",
      isInstantBooking: true,
      isWishlisted: false,
      features: ["Photography Guide", "Equipment Rental", "Hot Drinks", "Small Group"]
    },
    {
      id: 7,
      title: "Santorini Sunset Sailing & Wine Tasting",
      description: "Sail around the beautiful Santorini caldera while enjoying local wines and watching the famous sunset. Includes swimming stops and traditional Greek snacks.",
      image: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&h=600&fit=crop",
      location: "Santorini, Greece",
      category: "Beach & Water Sports",
      price: 110,
      originalPrice: null,
      discount: null,
      rating: 4.7,
      reviewCount: 678,
      duration: "5 hours",
      maxGroupSize: 16,
      difficulty: "Easy",
      availability: "high",
      isInstantBooking: true,
      isWishlisted: false,
      features: ["Wine Tasting", "Swimming Stops", "Sunset Views", "Greek Snacks"]
    },
    {
      id: 8,
      title: "Louvre Museum Private Guided Tour",
      description: "Skip the lines and explore the world\'s largest art museum with a private guide. Discover masterpieces including the Mona Lisa and Venus de Milo.",
      image: "https://images.unsplash.com/photo-1566139884456-8c0c8e2e8e8e?w=800&h=600&fit=crop",
      location: "Paris, France",
      category: "Art & Museums",
      price: 89,
      originalPrice: 105,
      discount: 15,
      rating: 4.6,
      reviewCount: 423,
      duration: "3 hours",
      maxGroupSize: 6,
      difficulty: "Easy",
      availability: "medium",
      isInstantBooking: false,
      isWishlisted: true,
      features: ["Skip the Line", "Private Guide", "Headsets", "Museum Map"]
    }
  ];

  // Initialize tours and apply filters
  useEffect(() => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setTours(mockTours);
      setLoading(false);
    }, 1500);
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...tours];

    // Apply search query
    if (searchQuery) {
      filtered = filtered?.filter(tour =>
        tour?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        tour?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        tour?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        tour?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply price filter
    if (activeFilters?.priceMin !== undefined || activeFilters?.priceMax !== undefined) {
      filtered = filtered?.filter(tour =>
        tour?.price >= (activeFilters?.priceMin || 0) &&
        tour?.price <= (activeFilters?.priceMax || 5000)
      );
    }

    // Apply duration filter
    if (activeFilters?.duration) {
      filtered = filtered?.filter(tour => {
        const duration = tour?.duration?.toLowerCase();
        switch (activeFilters?.duration) {
          case '1-3': return duration?.includes('hour') && !duration?.includes('4') && !duration?.includes('5') && !duration?.includes('6') && !duration?.includes('7') && !duration?.includes('8');
          case '4-8': return duration?.includes('hour') && (duration?.includes('4') || duration?.includes('5') || duration?.includes('6') || duration?.includes('7') || duration?.includes('8'));
          case 'full-day': return duration?.includes('8 hours') || duration?.includes('full day');
          case '2-3-days': return duration?.includes('day') && !duration?.includes('full');
          case '1-week': return duration?.includes('week');
          default: return true;
        }
      });
    }

    // Apply category filter
    if (activeFilters?.categories && activeFilters?.categories?.length > 0) {
      filtered = filtered?.filter(tour =>
        activeFilters?.categories?.includes(tour?.category)
      );
    }

    // Apply rating filter
    if (activeFilters?.rating) {
      filtered = filtered?.filter(tour =>
        tour?.rating >= parseFloat(activeFilters?.rating)
      );
    }

    // Apply difficulty filter
    if (activeFilters?.difficulty) {
      filtered = filtered?.filter(tour =>
        tour?.difficulty?.toLowerCase() === activeFilters?.difficulty?.toLowerCase()
      );
    }

    // Apply quick filters
    if (activeFilters?.quickFilters && activeFilters?.quickFilters?.length > 0) {
      filtered = filtered?.filter(tour => {
        return activeFilters?.quickFilters?.some(filter => {
          switch (filter) {
            case 'Free Cancellation': return tour?.features?.includes('Free Cancellation');
            case 'Instant Booking': return tour?.isInstantBooking;
            case 'Small Groups': return tour?.maxGroupSize <= 10;
            case 'Skip the Line': return tour?.features?.includes('Skip the Line');
            case 'Private Tour': return tour?.features?.includes('Private Guide') || tour?.maxGroupSize <= 6;
            default: return false;
          }
        });
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered?.sort((a, b) => a?.price - b?.price);
        break;
      case 'price-high':
        filtered?.sort((a, b) => b?.price - a?.price);
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'duration':
        filtered?.sort((a, b) => {
          const getDurationHours = (duration) => {
            if (duration?.includes('hour')) return parseInt(duration);
            if (duration?.includes('day')) return parseInt(duration) * 24;
            if (duration?.includes('week')) return parseInt(duration) * 168;
            return 0;
          };
          return getDurationHours(a?.duration) - getDurationHours(b?.duration);
        });
        break;
      case 'popularity':
        filtered?.sort((a, b) => b?.reviewCount - a?.reviewCount);
        break;
      case 'newest':
        filtered?.sort((a, b) => b?.id - a?.id);
        break;
      default: // relevance
        break;
    }

    setFilteredTours(filtered);
  }, [tours, searchQuery, activeFilters, sortBy]);

  // Handle search
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSearchParams(query ? { q: query } : {});
    setPage(1);
  }, [setSearchParams]);

  // Handle filter application
  const handleApplyFilters = useCallback((filters) => {
    setActiveFilters(filters);
    setPage(1);
  }, []);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setActiveFilters({});
    setPage(1);
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    if (mode === 'map') {
      setSelectedTour(null);
    }
  }, []);

  // Handle tour selection (for map view)
  const handleTourSelect = useCallback((tour) => {
    setSelectedTour(tour);
  }, []);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    setPage(prev => prev + 1);
    // In a real app, this would load more data
    // For demo, we'll just simulate it
    setTimeout(() => {
      setHasMore(false); // Simulate no more data
    }, 1000);
  }, []);

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters?.priceMin > 0 || activeFilters?.priceMax < 5000) count++;
    if (activeFilters?.duration) count++;
    if (activeFilters?.categories?.length > 0) count++;
    if (activeFilters?.rating) count++;
    if (activeFilters?.difficulty) count++;
    if (activeFilters?.quickFilters?.length > 0) count++;
    return count;
  };

  // Handle empty state actions
  const handleNewSearch = (query = '') => {
    setSearchQuery(query);
    setSearchParams(query ? { q: query } : {});
    setActiveFilters({});
  };

  const handleBrowseAll = () => {
    navigate('/homepage');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        {/* Filter Sidebar */}
        <FilterSidebar
          isOpen={isFiltersOpen}
          onClose={() => setIsFiltersOpen(false)}
          onApplyFilters={handleApplyFilters}
          activeFilters={activeFilters}
        />

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ${
          isFiltersOpen ? 'lg:ml-80' : 'lg:ml-80'
        }`}>
          {/* Search Header */}
          <SearchHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearch={handleSearch}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            resultsCount={filteredTours?.length}
            onToggleFilters={() => setIsFiltersOpen(!isFiltersOpen)}
            isFiltersOpen={isFiltersOpen}
          />

          {/* Active Filters Display */}
          {getActiveFilterCount() > 0 && (
            <div className="px-4 lg:px-6 py-3 bg-muted/50 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">Active filters:</span>
                  <span className="text-sm font-medium text-foreground">
                    {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  iconName="X"
                  iconPosition="left"
                >
                  Clear All
                </Button>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="px-4 lg:px-6 py-6">
            {loading ? (
              <LoadingSkeleton viewMode={viewMode} count={6} />
            ) : filteredTours?.length === 0 ? (
              <EmptyState
                searchQuery={searchQuery}
                hasActiveFilters={getActiveFilterCount() > 0}
                onClearFilters={handleClearFilters}
                onNewSearch={handleNewSearch}
                onBrowseAll={handleBrowseAll}
              />
            ) : viewMode === 'map' ? (
              <div className="h-[calc(100vh-200px)]">
                <MapView
                  tours={filteredTours}
                  onTourSelect={handleTourSelect}
                  selectedTour={selectedTour}
                />
              </div>
            ) : (
              <>
                {/* Results Grid/List */}
                <div className={`${
                  viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' :'space-y-6'
                }`}>
                  {filteredTours?.map((tour) => (
                    <TourCard
                      key={tour?.id}
                      tour={tour}
                      viewMode={viewMode}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && filteredTours?.length > 0 && (
                  <div className="flex justify-center mt-12">
                    <Button
                      variant="outline"
                      onClick={handleLoadMore}
                      iconName="ChevronDown"
                      iconPosition="right"
                      className="px-8"
                    >
                      Load More Tours
                    </Button>
                  </div>
                )}

                {/* Results Summary */}
                {filteredTours?.length > 0 && (
                  <div className="text-center mt-8 pt-8 border-t border-border">
                    <p className="text-muted-foreground">
                      Showing {filteredTours?.length} of {tours?.length} tours
                      {searchQuery && ` for "${searchQuery}"`}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 w-12 h-12 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors z-40 flex items-center justify-center"
      >
        <Icon name="ChevronUp" size={20} />
      </button>
    </div>
  );
};

export default SearchResults;